/**
 * AdvertiseHereCard - rendered into a SponsoredSlot when no live ad
 * matches the surface's topic + state filter. Same chrome as a real
 * sponsored card so the page rhythm is unchanged, but tinted cool
 * grey rather than amber so it doesn't pretend to be an ad.
 *
 * CTA opens the AdvertisingEnquiryModal pre-filled with the surface
 * (so the team can see which page generated the lead).
 *
 * 2026-09-08: Copy is now surface-tailored via `getSurfaceMessaging`.
 * An empty slot on `/hbcf-adelaide-explainer` reads "Reach SA
 * families researching HBCF cover" instead of a generic prompt.
 */
import React, { useState } from 'react';
import AdvertisingEnquiryModal from './AdvertisingEnquiryModal';
import { getSurfaceMessaging } from './adSurfaceMessaging';

const AdvertiseHereCard = ({ surface, state, size = 'small', format }) => {
  const resolvedSize = format || size;
  const [showEnquiry, setShowEnquiry] = useState(false);
  const { title, body, region } = getSurfaceMessaging(surface, state);
  const aspect = resolvedSize === 'medium' ? '800 / 250' : resolvedSize === 'square' ? '1 / 1' : resolvedSize === 'large' ? '9 / 16' : '800 / 150';

  return (
    <>
      <div
        className="bp-advertise-here group relative w-full overflow-hidden"
        data-testid={`advertise-here-${surface || 'unknown'}`}
        style={{
          borderRadius: 16,
          border: '1px dashed rgba(15, 23, 42, 0.18)',
          background: '#fafbfc',
          padding: '14px 16px',
          aspectRatio: aspect,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: '#f3f4f6',
              color: '#374151',
              border: '1px solid rgba(15, 23, 42, 0.10)',
              borderRadius: 999,
              padding: '2px 8px',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            <span aria-hidden="true">▸</span> Available
          </span>
        </div>

        <div style={{ textAlign: 'center', padding: format === 'hero' ? '20px 8px 8px' : '8px 8px 4px' }}>
          <p style={{ margin: 0, fontSize: 14, color: '#0a2540', fontWeight: 600, lineHeight: 1.35 }}>
            {title}
          </p>
          <p style={{ margin: '6px 0 12px', fontSize: 12.5, color: '#6b7280', lineHeight: 1.5 }}>
            {body}
          </p>
          <button
            type="button"
            onClick={() => setShowEnquiry(true)}
            data-testid="advertise-here-cta"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px',
              background: '#0a2540',
              color: '#ffffff',
              border: 0,
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background-color 160ms ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#1d8efe'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#0a2540'; }}
          >
            Enquire about advertising →
          </button>
        </div>
      </div>

      {showEnquiry && (
        <AdvertisingEnquiryModal
          sourcePage={surface}
          defaultState={state}
          onClose={() => setShowEnquiry(false)}
        />
      )}
    </>
  );
};

export default AdvertiseHereCard;
